import {
  type AnnotationCapability,
  type EmbedPdfContainer,
  PDFViewer,
  PdfAnnotationBorderStyle,
  PdfAnnotationSubtype,
  ZoomMode,
  type PDFViewerConfig,
  type PDFViewerRef,
  type DocumentManagerCapability,
  type PdfHighlightAnnoObject,
  type PdfSquareAnnoObject,
  type PluginRegistry,
  type Rect,
  type ScrollCapability,
  type SearchCapability,
  type SelectionCapability,
  type TrackedAnnotation,
  GroupItem,
  ToolbarItem,
  CommandsPlugin,
  UIPlugin
} from '@embedpdf/react-pdf-viewer';
import clsx from 'clsx';
import type { PageChangeEvent } from '@embedpdf/plugin-scroll';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import { embedPdfTheme } from '../../tokenStyle/tokeReset';

import type {
  PdfViewerAnnotationRequest,
  PdfViewerAnnotationReference,
  PdfViewerAnnotationResult,
  PdfViewerAnnotationSelection,
  PdfViewerAnnotationStyle,
  PdfViewerApplyPreparedAnnotationBatchOptions,
  PdfViewerClearAnnotationsMode,
  PdfViewerPreparedAnnotationBatch,
  PdfViewerPreparedAnnotationBatchMap,
  PdfViewerPreparedAnnotationDescriptor,
  PdfViewerPreparedFallbackAnnotationDescriptor,
  PdfViewerPreparedFallbackAnnotationRect,
  PdfViewerPreparedHighlightAnnotationDescriptor,
  PdfViewerPreparedAnnotationBatchResult,
  PdfViewerRectangleAnnotationInput,
  PdfViewerRectangleAnnotationResult,
  PdfViewerTextSelectionMatch,
  SearchablePdfViewerHandle
} from './searchablePdfViewer';
import {
  SEARCH_HIGHLIGHT_STYLE_ID,
  SEARCH_HIGHLIGHT_STYLE_TEXT
} from './searchHighlightTheme';
// import { fallbackPdfPageItems } from './search/pdfFallbackPageItems';
import {
  countPdfFallbackSearchInputItems,
  findPdfFallbackMatches,
  PdfFallbackPageItemsByPage,
  type PdfFallbackBoundary
} from './search/pdfFallbackSearch';

export type PdfBufferSource = ArrayBuffer | Uint8Array;

interface PdfBufferViewerProps {
  buffer?: PdfBufferSource | null;
  className?: string;
  fileName: string;
  fallbackPdfPageItems: PdfFallbackPageItemsByPage;
  onCurrentPageChange?: (pageNumber: number) => void;
  onAnnotationReferenceSelect?: (
    selection: PdfViewerAnnotationSelection
  ) => void;
  onTextSelectionMatch?: (selection: PdfViewerTextSelectionMatch) => void;
  sourceUrl?: string | null;
}

export type PdfBufferViewerHandle = SearchablePdfViewerHandle;

const DISABLED_VIEWER_CATEGORIES = [
  'annotation',
  'annotation-shape',
  'annotation-rectangle',
  'annotation-circle',
  'annotation-line',
  'annotation-arrow',
  'annotation-polygon',
  'annotation-polyline',
  'document',
  'page-settings',
  // "selection-copy",
  // "document-open",
  // "document-print",
  // "document-export",
  'panel-comment',
  // 'panel-search',
  'panel-view',
  'redaction',
  'form',
  'insert'
] as const;

const PROGRAMMATIC_SEARCH_ANNOTATION_COLOR = '#00ff2f';
const PROGRAMMATIC_SEARCH_ANNOTATION_OPACITY = 0.4;
const PROGRAMMATIC_SEARCH_ANNOTATION_SOURCE = 'programmatic-search-highlight';
const PROGRAMMATIC_ACTIVE_SEARCH_ANNOTATION_COLOR = '#FBF719';
const PROGRAMMATIC_ACTIVE_SELECTION_ANNOTATION_OPACITY = 0.08;
const PROGRAMMATIC_ACTIVE_SELECTION_ANNOTATION_STROKE_OPACITY = 1;
const PROGRAMMATIC_ACTIVE_SELECTION_ANNOTATION_STROKE_WIDTH = 3;
const PROGRAMMATIC_ACTIVE_SELECTION_ANNOTATION_HORIZONTAL_SCALE = 1.1;
const PROGRAMMATIC_ACTIVE_SELECTION_ANNOTATION_VERTICAL_SCALE = 1.22;
const PROGRAMMATIC_ACTIVE_SEARCH_ANNOTATION_SOURCE =
  'programmatic-active-search-highlight';
const PROGRAMMATIC_FALLBACK_SEARCH_ANNOTATION_OPACITY =
  PROGRAMMATIC_SEARCH_ANNOTATION_OPACITY;
const PROGRAMMATIC_FALLBACK_SEARCH_ANNOTATION_SOURCE =
  'programmatic-fallback-search-highlight';
const PROGRAMMATIC_ACTIVE_FALLBACK_SEARCH_ANNOTATION_SOURCE =
  'programmatic-active-fallback-search-highlight';
const PROGRAMMATIC_RECTANGLE_ANNOTATION_SOURCE = 'programmatic-rectangle';
const PROGRAMMATIC_RECTANGLE_ANNOTATION_COLOR = '#60a5fa';
const PROGRAMMATIC_RECTANGLE_ANNOTATION_STROKE_COLOR = '#2563eb';
const CUSTOM_SMILEY_COMMAND_ID = 'custom.smiley';
const CUSTOM_SMILEY_BUTTON_ID = 'smiley-button';
const TEXT_SELECTION_TOOLBAR_CATEGORY = 'text-selection-toolbar';

type PreparedHighlightAnnotationDescriptor =
  PdfViewerPreparedHighlightAnnotationDescriptor;

type PdfFallbackAnnotationRect = PdfViewerPreparedFallbackAnnotationRect;

type PreparedFallbackAnnotationDescriptor =
  PdfViewerPreparedFallbackAnnotationDescriptor;

type PreparedAnnotationDescriptor = PdfViewerPreparedAnnotationDescriptor;

type PreparedAnnotationBatch = PdfViewerPreparedAnnotationBatch;

function normalizeAnnotationStyle(
  annotationStyle?: PdfViewerAnnotationStyle
): PdfViewerAnnotationStyle {
  return annotationStyle === 'active-selection'
    ? 'active-selection'
    : 'default';
}

function getHighlightAnnotationColor(
  annotationStyle?: PdfViewerAnnotationStyle
) {
  return normalizeAnnotationStyle(annotationStyle) === 'active-selection'
    ? PROGRAMMATIC_ACTIVE_SEARCH_ANNOTATION_COLOR
    : PROGRAMMATIC_SEARCH_ANNOTATION_COLOR;
}

function getHighlightAnnotationOpacity(
  annotationStyle?: PdfViewerAnnotationStyle
) {
  return normalizeAnnotationStyle(annotationStyle) === 'active-selection'
    ? PROGRAMMATIC_ACTIVE_SELECTION_ANNOTATION_OPACITY
    : PROGRAMMATIC_SEARCH_ANNOTATION_OPACITY;
}

function getHighlightAnnotationSource(
  annotationStyle?: PdfViewerAnnotationStyle
) {
  return normalizeAnnotationStyle(annotationStyle) === 'active-selection'
    ? PROGRAMMATIC_ACTIVE_SEARCH_ANNOTATION_SOURCE
    : PROGRAMMATIC_SEARCH_ANNOTATION_SOURCE;
}

function getFallbackAnnotationOpacity(
  annotationStyle?: PdfViewerAnnotationStyle
) {
  return normalizeAnnotationStyle(annotationStyle) === 'active-selection'
    ? PROGRAMMATIC_ACTIVE_SELECTION_ANNOTATION_OPACITY
    : PROGRAMMATIC_FALLBACK_SEARCH_ANNOTATION_OPACITY;
}

function getFallbackAnnotationSource(
  annotationStyle?: PdfViewerAnnotationStyle
) {
  return normalizeAnnotationStyle(annotationStyle) === 'active-selection'
    ? PROGRAMMATIC_ACTIVE_FALLBACK_SEARCH_ANNOTATION_SOURCE
    : PROGRAMMATIC_FALLBACK_SEARCH_ANNOTATION_SOURCE;
}

function normalizePdfBuffer(source: PdfBufferSource): ArrayBuffer {
  if (source instanceof ArrayBuffer) {
    return source.slice(0);
  }

  return Uint8Array.from(source).buffer;
}

function normalizeSelectedText(chunks: string[]) {
  return chunks
    .map((chunk) => chunk.trim())
    .filter(Boolean)
    .join(' ')
    .trim();
}

function getSearchCapability(registry: PluginRegistry) {
  return registry.getPlugin('search')?.provides?.() as
    | SearchCapability
    | undefined;
}

function getSelectionCapability(registry: PluginRegistry) {
  return registry.getPlugin('selection')?.provides?.() as
    | SelectionCapability
    | undefined;
}

function getAnnotationCapability(registry: PluginRegistry) {
  return registry.getPlugin('annotation')?.provides?.() as
    | AnnotationCapability
    | undefined;
}

function getDocumentManagerCapability(registry: PluginRegistry) {
  return registry.getPlugin('document-manager')?.provides?.() as
    | DocumentManagerCapability
    | undefined;
}

function getScrollCapability(registry: PluginRegistry) {
  return registry.getPlugin('scroll')?.provides?.() as
    | ScrollCapability
    | undefined;
}

function installSearchHighlightStyles(container: EmbedPdfContainer | null) {
  const shadowRoot = container?.shadowRoot;

  if (!shadowRoot || shadowRoot.getElementById(SEARCH_HIGHLIGHT_STYLE_ID)) {
    return;
  }

  const styleElement = document.createElement('style');
  styleElement.id = SEARCH_HIGHLIGHT_STYLE_ID;
  styleElement.textContent = SEARCH_HIGHLIGHT_STYLE_TEXT;
  shadowRoot.appendChild(styleElement);
}

function mergeRects(rects: Rect[]): Rect {
  const firstRect = rects[0];

  return rects.reduce(
    (combinedRect, rect) => {
      const combinedLeft = combinedRect.origin.x;
      const combinedTop = combinedRect.origin.y;
      const combinedRight = combinedLeft + combinedRect.size.width;
      const combinedBottom = combinedTop + combinedRect.size.height;
      const rectLeft = rect.origin.x;
      const rectTop = rect.origin.y;
      const rectRight = rectLeft + rect.size.width;
      const rectBottom = rectTop + rect.size.height;
      const nextLeft = Math.min(combinedLeft, rectLeft);
      const nextTop = Math.min(combinedTop, rectTop);
      const nextRight = Math.max(combinedRight, rectRight);
      const nextBottom = Math.max(combinedBottom, rectBottom);

      return {
        origin: {
          x: nextLeft,
          y: nextTop
        },
        size: {
          height: nextBottom - nextTop,
          width: nextRight - nextLeft
        }
      };
    },
    {
      origin: {
        x: firstRect.origin.x,
        y: firstRect.origin.y
      },
      size: {
        height: firstRect.size.height,
        width: firstRect.size.width
      }
    }
  );
}

function cloneRect(rect: Rect): Rect {
  return {
    origin: {
      x: rect.origin.x,
      y: rect.origin.y
    },
    size: {
      height: rect.size.height,
      width: rect.size.width
    }
  };
}

function normalizeQueries(query: string | string[]) {
  const sourceQueries = Array.isArray(query) ? query : [query];

  return [
    ...new Set(
      sourceQueries.map((item) => normalizeQueryText(item)).filter(Boolean)
    )
  ];
}

function normalizePageNumbers(pageNumbers: Array<number | null | undefined>) {
  return Array.from(
    new Set(
      pageNumbers.filter(
        (pageNumber): pageNumber is number =>
          typeof pageNumber === 'number' &&
          Number.isInteger(pageNumber) &&
          pageNumber > 0
      )
    )
  ).sort((leftPageNumber, rightPageNumber) => leftPageNumber - rightPageNumber);
}

function normalizeContextIds(contextIds: Array<string | null | undefined>) {
  return Array.from(
    new Set(
      contextIds
        .map((contextId) =>
          typeof contextId === 'string' ? contextId.trim() : ''
        )
        .filter(Boolean)
    )
  ).sort((leftContextId, rightContextId) =>
    leftContextId.localeCompare(rightContextId)
  );
}

function getRequestedPageNumbers(
  request: Pick<
    PdfViewerAnnotationRequest,
    'pageNumber' | 'pageNumbers' | 'reference'
  >
) {
  return normalizePageNumbers([
    ...(request.pageNumbers ?? []),
    request.pageNumber,
    ...(request.reference?.pageNumbers ?? [])
  ]);
}

function normalizeQueryText(input: string) {
  return input
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(
      /['\u0060\u00B4\u02B9\u02BB\u02BC\u02BD\u02BE\u02BF\u0374\u0384\u055A\u05F3\u07F4\u07F5\u2018\u2019\u201A\u201B\u2032\u2035\u275B\u275C\uA78C\uFF07]/g,
      "'"
    )
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')
    .replace(/[\u2010\u2011\u2012\u2013\u2014\u2212]/g, '-')
    .replace(/[\u00A0\u2000-\u200B\u202F\u205F\u3000]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function createAnnotationId() {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }

  return `annotation-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function buildAnnotationSignature(
  query: string,
  pageIndex: number,
  charIndex: number,
  charCount: number,
  candidateCardKey?: string
) {
  return [
    query.toLowerCase(),
    pageIndex,
    charIndex,
    charCount,
    candidateCardKey ?? ''
  ].join('::');
}

function cloneAnnotationReference(
  reference?: PdfViewerAnnotationReference
): PdfViewerAnnotationReference | undefined {
  if (!reference?.candidateCardKey) {
    return undefined;
  }

  const normalizedContextIds = normalizeContextIds([
    ...(reference.contextIds ?? []),
    reference.contextId
  ]);

  return {
    candidateCardKey: reference.candidateCardKey,
    candidateId: reference.candidateId,
    contextId: normalizedContextIds[0] ?? reference.contextId,
    contextIds:
      normalizedContextIds.length > 0 ? normalizedContextIds : undefined,
    pageNumbers: reference.pageNumbers ? [...reference.pageNumbers] : undefined,
    // sentenceIds: reference.sentenceIds ? [...reference.sentenceIds] : undefined,
    sourceText: reference.sourceText
  };
}

function getAnnotationReferenceFromCustom(
  custom: unknown
): PdfViewerAnnotationReference | null {
  if (!custom || typeof custom !== 'object' || !('reference' in custom)) {
    return null;
  }

  const reference = custom.reference;

  if (!reference || typeof reference !== 'object') {
    return null;
  }

  const candidateCardKey =
    'candidateCardKey' in reference &&
    typeof reference.candidateCardKey === 'string'
      ? reference.candidateCardKey
      : '';

  if (!candidateCardKey) {
    return null;
  }

  const sentenceIds =
    'sentenceIds' in reference && Array.isArray(reference.sentenceIds)
      ? reference.sentenceIds.filter(
          (value): value is string => typeof value === 'string'
        )
      : undefined;
  const pageNumbers =
    'pageNumbers' in reference && Array.isArray(reference.pageNumbers)
      ? normalizePageNumbers(
          reference.pageNumbers.map((value) =>
            typeof value === 'number' ? value : null
          )
        )
      : undefined;
  const contextIds = normalizeContextIds([
    ...('contextIds' in reference && Array.isArray(reference.contextIds)
      ? reference.contextIds.map((value) =>
          typeof value === 'string' ? value : null
        )
      : []),
    'contextId' in reference && typeof reference.contextId === 'string'
      ? reference.contextId
      : null
  ]);

  return {
    candidateCardKey,
    candidateId:
      'candidateId' in reference && typeof reference.candidateId === 'string'
        ? reference.candidateId
        : undefined,
    contextId: contextIds[0] ?? undefined,
    contextIds: contextIds.length > 0 ? contextIds : undefined,
    pageNumbers,
    sentenceIds,
    sourceText:
      'sourceText' in reference && typeof reference.sourceText === 'string'
        ? reference.sourceText
        : undefined
  };
}

function getAnnotationStyleFromCustom(
  custom: unknown
): PdfViewerAnnotationStyle | undefined {
  if (
    !custom ||
    typeof custom !== 'object' ||
    !('annotationStyle' in custom)
  ) {
    return undefined;
  }

  return custom.annotationStyle === 'active-selection'
    ? 'active-selection'
    : custom.annotationStyle === 'default'
      ? 'default'
      : undefined;
}

function getExistingAnnotationSignatures(
  annotation: AnnotationCapability,
  source: string
) {
  const signatures = new Set<string>();

  for (const trackedAnnotation of annotation.getAnnotations()) {
    const custom = trackedAnnotation.object.custom;

    if (
      custom &&
      typeof custom === 'object' &&
      'signature' in custom &&
      'source' in custom &&
      custom.source === source &&
      typeof custom.signature === 'string'
    ) {
      signatures.add(custom.signature);
    }
  }

  return signatures;
}

function createHighlightAnnotation(
  query: string,
  pageIndex: number,
  charIndex: number,
  charCount: number,
  rects: Rect[],
  reference?: PdfViewerAnnotationReference,
  annotationStyle?: PdfViewerAnnotationStyle
): PdfHighlightAnnoObject | PdfSquareAnnoObject {
  const segmentRects = rects.map(cloneRect);
  const normalizedReference = cloneAnnotationReference(reference);
  const normalizedAnnotationStyle = normalizeAnnotationStyle(annotationStyle);
  const signature = buildAnnotationSignature(
    query,
    pageIndex,
    charIndex,
    charCount,
    normalizedReference?.candidateCardKey
  );

  if (normalizedAnnotationStyle === 'active-selection') {
    return createActiveSelectionOutlineAnnotation(
      query,
      pageIndex,
      createBoundaryRectFromViewerRect(mergeRects(segmentRects)),
      normalizedReference,
      getHighlightAnnotationSource(normalizedAnnotationStyle),
      signature,
      {
        charCount,
        charIndex
      }
    );
  }

  return {
    contents: query,
    custom: {
      annotationStyle: normalizedAnnotationStyle,
      charCount,
      charIndex,
      query,
      signature,
      reference: normalizedReference,
      source: getHighlightAnnotationSource(normalizedAnnotationStyle)
    },
    id: createAnnotationId(),
    opacity: getHighlightAnnotationOpacity(normalizedAnnotationStyle),
    pageIndex,
    rect: mergeRects(segmentRects),
    segmentRects,
    strokeColor: getHighlightAnnotationColor(normalizedAnnotationStyle),
    type: PdfAnnotationSubtype.HIGHLIGHT
  };
}

function createViewerRect(rect: PdfFallbackAnnotationRect): Rect {
  return {
    origin: {
      x: rect.x,
      y: rect.y
    },
    size: {
      height: rect.height,
      width: rect.width
    }
  };
}

function createBoundaryRectFromViewerRect(rect: Rect): PdfFallbackAnnotationRect {
  return {
    height: rect.size.height,
    width: rect.size.width,
    x: rect.origin.x,
    y: rect.origin.y
  };
}

function expandBoundaryRect(
  rect: PdfFallbackAnnotationRect,
  widthScale = PROGRAMMATIC_ACTIVE_SELECTION_ANNOTATION_HORIZONTAL_SCALE,
  heightScale = PROGRAMMATIC_ACTIVE_SELECTION_ANNOTATION_VERTICAL_SCALE
): PdfFallbackAnnotationRect {
  if (widthScale <= 1 && heightScale <= 1) {
    return cloneFallbackBoundary(rect);
  }

  const widthDelta = rect.width * Math.max(widthScale - 1, 0);
  const heightDelta = rect.height * Math.max(heightScale - 1, 0);

  return {
    height: rect.height + heightDelta,
    width: rect.width + widthDelta,
    x: rect.x - widthDelta / 2,
    y: rect.y - heightDelta / 2
  };
}

function createActiveSelectionOutlineAnnotation(
  query: string,
  pageIndex: number,
  rect: PdfFallbackAnnotationRect,
  reference: PdfViewerAnnotationReference | undefined,
  source: string,
  signature: string,
  extraCustom: Record<string, number> = {}
): PdfSquareAnnoObject {
  const expandedRect = expandBoundaryRect(rect);

  return {
    color: 'transparent',
    contents: query,
    custom: {
      ...extraCustom,
      annotationStyle: 'active-selection',
      query,
      reference,
      signature,
      source
    },
    flags: ['print'],
    id: createAnnotationId(),
    opacity: PROGRAMMATIC_ACTIVE_SELECTION_ANNOTATION_STROKE_OPACITY,
    pageIndex,
    rect: createViewerRect(expandedRect),
    strokeColor: PROGRAMMATIC_ACTIVE_SEARCH_ANNOTATION_COLOR,
    strokeStyle: PdfAnnotationBorderStyle.SOLID,
    strokeWidth: PROGRAMMATIC_ACTIVE_SELECTION_ANNOTATION_STROKE_WIDTH,
    type: PdfAnnotationSubtype.SQUARE
  };
}

function cloneFallbackBoundary(
  rect: PdfFallbackAnnotationRect
): PdfFallbackAnnotationRect {
  return {
    height: rect.height,
    width: rect.width,
    x: rect.x,
    y: rect.y
  };
}

function mergeFallbackBoundaries(
  rects: PdfFallbackAnnotationRect[]
): PdfFallbackAnnotationRect | null {
  const firstRect = rects[0];

  if (!firstRect) {
    return null;
  }

  return rects.reduce(
    (combinedRect, rect) => {
      const combinedRight = combinedRect.x + combinedRect.width;
      const combinedBottom = combinedRect.y + combinedRect.height;
      const rectRight = rect.x + rect.width;
      const rectBottom = rect.y + rect.height;
      const nextLeft = Math.min(combinedRect.x, rect.x);
      const nextTop = Math.min(combinedRect.y, rect.y);
      const nextRight = Math.max(combinedRight, rectRight);
      const nextBottom = Math.max(combinedBottom, rectBottom);

      return {
        height: nextBottom - nextTop,
        width: nextRight - nextLeft,
        x: nextLeft,
        y: nextTop
      };
    },
    cloneFallbackBoundary(firstRect)
  );
}

function buildActiveSelectionFallbackBoundaries(
  results: ReturnType<typeof findPdfFallbackMatches>
): PdfFallbackBoundary[] {
  const boundaries: PdfFallbackBoundary[] = [];

  for (const result of results) {
    if (result.boundary) {
      boundaries.push({
        height: result.boundary.height,
        pageNumber: result.boundary.pageNumber,
        width: result.boundary.width,
        x: result.boundary.x,
        y: result.boundary.y
      });
      continue;
    }

    const rectsByPage = new Map<number, PdfFallbackAnnotationRect[]>();

    for (const item of result.dimensionItems) {
      const pageRects = rectsByPage.get(item.pageNumber) ?? [];
      pageRects.push({
        height: item.height,
        width: item.width,
        x: item.x,
        y: item.y
      });
      rectsByPage.set(item.pageNumber, pageRects);
    }

    for (const [pageNumber, pageRects] of rectsByPage.entries()) {
      const mergedBoundary = mergeFallbackBoundaries(pageRects);

      if (!mergedBoundary) {
        continue;
      }

      boundaries.push({
        ...mergedBoundary,
        pageNumber
      });
    }
  }

  return boundaries.sort((leftBoundary, rightBoundary) => {
    if (leftBoundary.pageNumber !== rightBoundary.pageNumber) {
      return leftBoundary.pageNumber - rightBoundary.pageNumber;
    }

    if (leftBoundary.y !== rightBoundary.y) {
      return leftBoundary.y - rightBoundary.y;
    }

    return leftBoundary.x - rightBoundary.x;
  });
}

function clonePreparedAnnotationDescriptor(
  descriptor: PreparedAnnotationDescriptor
): PreparedAnnotationDescriptor {
  if (descriptor.kind === 'highlight') {
    return {
      ...descriptor,
      rects: descriptor.rects.map(cloneRect),
      reference: cloneAnnotationReference(descriptor.reference)
    };
  }

  return {
    ...descriptor,
    rect: cloneFallbackBoundary(descriptor.rect),
    reference: cloneAnnotationReference(descriptor.reference)
  };
}

function clonePreparedAnnotationBatch(
  batch: PreparedAnnotationBatch
): PreparedAnnotationBatch {
  return {
    ...batch,
    descriptors: batch.descriptors.map(clonePreparedAnnotationDescriptor)
  };
}

function clonePreparedAnnotationBatchMap(
  batches: PdfViewerPreparedAnnotationBatchMap
): PdfViewerPreparedAnnotationBatchMap {
  return Object.fromEntries(
    Object.entries(batches).map(([cacheKey, batch]) => [
      cacheKey,
      clonePreparedAnnotationBatch({
        ...batch,
        cacheKey
      })
    ])
  );
}

function formatAnnotationRectValue(value: number) {
  return value.toFixed(4);
}

function buildRectangleAnnotationSignature(
  query: string,
  pageIndex: number,
  rect: PdfFallbackAnnotationRect,
  candidateCardKey?: string
) {
  return [
    query.toLowerCase(),
    pageIndex,
    formatAnnotationRectValue(rect.x),
    formatAnnotationRectValue(rect.y),
    formatAnnotationRectValue(rect.width),
    formatAnnotationRectValue(rect.height),
    candidateCardKey ?? ''
  ].join('::');
}

function createFallbackSearchRectangleAnnotation(
  query: string,
  pageIndex: number,
  rect: PdfFallbackAnnotationRect,
  reference?: PdfViewerAnnotationReference,
  annotationStyle?: PdfViewerAnnotationStyle
): PdfSquareAnnoObject {
  const normalizedReference = cloneAnnotationReference(reference);
  const normalizedAnnotationStyle = normalizeAnnotationStyle(annotationStyle);
  const signature = buildRectangleAnnotationSignature(
    query,
    pageIndex,
    rect,
    normalizedReference?.candidateCardKey
  );

  if (normalizedAnnotationStyle === 'active-selection') {
    return createActiveSelectionOutlineAnnotation(
      query,
      pageIndex,
      cloneFallbackBoundary(rect),
      normalizedReference,
      getFallbackAnnotationSource(normalizedAnnotationStyle),
      signature
    );
  }

  const highlightAnnotationColor = getHighlightAnnotationColor(
    normalizedAnnotationStyle
  );

  return {
    color: highlightAnnotationColor,
    contents: query,
    custom: {
      annotationStyle: normalizedAnnotationStyle,
      query,
      signature,
      reference: normalizedReference,
      source: getFallbackAnnotationSource(normalizedAnnotationStyle)
    },
    flags: ['print'],
    id: createAnnotationId(),
    opacity: getFallbackAnnotationOpacity(normalizedAnnotationStyle),
    pageIndex,
    rect: createViewerRect(rect),
    strokeColor: highlightAnnotationColor,
    strokeStyle: PdfAnnotationBorderStyle.SOLID,
    strokeWidth: 2,
    type: PdfAnnotationSubtype.SQUARE
  };
}

function createRectangleAnnotation(
  rect: PdfViewerRectangleAnnotationInput,
  pageIndex: number
): PdfSquareAnnoObject {
  return {
    color: PROGRAMMATIC_RECTANGLE_ANNOTATION_COLOR,
    contents: 'Programmatic rectangle annotation',
    custom: {
      source: PROGRAMMATIC_RECTANGLE_ANNOTATION_SOURCE
    },
    flags: ['print'],
    id: createAnnotationId(),
    opacity: 0.22,
    pageIndex,
    rect: createViewerRect(rect),
    strokeColor: PROGRAMMATIC_RECTANGLE_ANNOTATION_STROKE_COLOR,
    strokeStyle: PdfAnnotationBorderStyle.SOLID,
    strokeWidth: 2,
    type: PdfAnnotationSubtype.SQUARE
  };
}

function getPreparedAnnotationDescriptorSource(
  descriptor: PreparedAnnotationDescriptor
) {
  return descriptor.kind === 'highlight'
    ? getHighlightAnnotationSource(descriptor.annotationStyle)
    : getFallbackAnnotationSource(descriptor.annotationStyle);
}

function buildPreparedAnnotationDeletionKey(
  descriptor: PreparedAnnotationDescriptor
) {
  return [
    descriptor.pageIndex,
    getPreparedAnnotationDescriptorSource(descriptor),
    descriptor.signature
  ].join('::');
}

function isProgrammaticAnnotationSource(value: unknown): boolean {
  return (
    value === PROGRAMMATIC_SEARCH_ANNOTATION_SOURCE ||
    value === PROGRAMMATIC_ACTIVE_SEARCH_ANNOTATION_SOURCE ||
    value === PROGRAMMATIC_FALLBACK_SEARCH_ANNOTATION_SOURCE ||
    value === PROGRAMMATIC_ACTIVE_FALLBACK_SEARCH_ANNOTATION_SOURCE ||
    value === PROGRAMMATIC_RECTANGLE_ANNOTATION_SOURCE
  );
}

function getAnnotationsBySource(
  annotation: AnnotationCapability,
  sources: string[]
) {
  const sourceSet = new Set(sources);

  return annotation
    .getAnnotations()
    .filter((trackedAnnotation: TrackedAnnotation) => {
      const custom = trackedAnnotation.object.custom;

      if (!custom || typeof custom !== 'object' || !('source' in custom)) {
        return false;
      }

      return sourceSet.has(custom.source as string);
    });
}

function getProgrammaticAnnotations(annotation: AnnotationCapability) {
  return getAnnotationsBySource(annotation, [
    PROGRAMMATIC_SEARCH_ANNOTATION_SOURCE,
    PROGRAMMATIC_ACTIVE_SEARCH_ANNOTATION_SOURCE,
    PROGRAMMATIC_FALLBACK_SEARCH_ANNOTATION_SOURCE,
    PROGRAMMATIC_ACTIVE_FALLBACK_SEARCH_ANNOTATION_SOURCE,
    PROGRAMMATIC_RECTANGLE_ANNOTATION_SOURCE
  ]);
}

function logFallbackSearchResult(
  query: string,
  results: ReturnType<typeof findPdfFallbackMatches>
) {
  const pageNumbers = Array.from(
    new Set(results.flatMap((result) => result.pageNumbers))
  ).sort((leftPageNumber, rightPageNumber) => leftPageNumber - rightPageNumber);
  const totalDimensionItems = results.reduce(
    (total, result) => total + result.dimensionItems.length,
    0
  );
  const totalMatchedItems = results.reduce(
    (total, result) => total + result.matchedItems.length,
    0
  );

  console.log('[PDF fallback search] result', {
    pageNumbers,
    query,
    results,
    totalDimensionItems,
    totalMatchedItems,
    totalMatches: results.length
  });
}

function logFallbackSearchRun(
  query: string,
  details: {
    fallbackItemCount: number;
    fallbackPageNumber?: number;
    fallbackPageNumbers?: number[];
    nativeResultCount: number;
  }
) {
  console.log('[PDF fallback search] running', {
    fallbackItemCount: details.fallbackItemCount,
    fallbackPageNumber: details.fallbackPageNumber ?? null,
    fallbackPageNumbers: details.fallbackPageNumbers ?? [],
    nativeResultCount: details.nativeResultCount,
    query
  });
}

function logFallbackSearchSkipped(query: string, nativeResultCount: number) {
  console.warn('[PDF fallback search] skipped', {
    nativeResultCount,
    query,
    reason: 'native-search-found-results'
  });
}

function logAnnotationSearchStart(
  query: string | string[],
  pageNumber?: number,
  pageNumbers?: number[]
) {
  console.log('[PDF annotations] annotateMatches called', {
    pageNumber: pageNumber ?? null,
    pageNumbers: pageNumbers ?? [],
    query
  });
}

function logAnnotationNativeSearchResult(query: string, totalResults: number) {
  console.log('[PDF annotations] native search result', {
    query,
    totalResults
  });
}

function logPdfViewerMounted() {
  console.log('[PDF viewer] component mounted');
}

function logPdfViewerImperativeHandleInitialized() {
  console.log('[PDF viewer] imperative handle initialized');
}

function logPdfViewerSearchCalled(query: string) {
  console.log('[PDF viewer] search called', {
    query
  });
}

function logPdfViewerSearchResult(query: string, totalResults: number) {
  console.log('[PDF viewer] search result', {
    query,
    totalResults
  });
}

function logPdfViewerClearSearchCalled() {
  console.log('[PDF viewer] clearSearch called');
}

function logPdfViewerClearAnnotationsCalled(
  mode: PdfViewerClearAnnotationsMode
) {
  console.log('[PDF viewer] clearAnnotations called', {
    mode
  });
}

function logPdfViewerRectangleAnnotationCalled(
  rect: PdfViewerRectangleAnnotationInput
) {
  console.log('[PDF viewer] createRectangleAnnotation called', rect);
}

function logPdfViewerPageBatchApply(details: {
  cacheKey: string;
  descriptorsToApply: number;
  pageNumber: number | null;
  totalBatchDescriptors: number;
}) {
  console.log('[PDF annotations] page create annotations fired', details);
}

function buildPreparedAnnotationBatchResult(
  batch: PreparedAnnotationBatch
): PdfViewerPreparedAnnotationBatchResult {
  return {
    activePageNumber: batch.activePageNumber,
    cacheKey: batch.cacheKey,
    preparedAnnotations: batch.descriptors.length,
    query: batch.query,
    skippedAnnotations: batch.skippedAnnotations,
    totalResults: batch.totalResults
  };
}

function buildPreparedAnnotationBatchRequestSignature(
  requests: PdfViewerAnnotationRequest[]
) {
  return JSON.stringify(
    requests.map((request) => ({
      annotationStyle: normalizeAnnotationStyle(request.annotationStyle),
      pageNumbers: getRequestedPageNumbers(request),
      queries: normalizeQueries(request.query),
      reference: request.reference
        ? {
            candidateCardKey: request.reference.candidateCardKey ?? '',
            candidateId: request.reference.candidateId ?? '',
            contextIds: normalizeContextIds([
              ...(request.reference.contextIds ?? []),
              request.reference.contextId
            ]),
            pageNumbers: normalizePageNumbers(
              request.reference.pageNumbers ?? []
            ),
            sentenceIds: [...(request.reference.sentenceIds ?? [])].sort(),
            sourceText: request.reference.sourceText?.trim() ?? ''
          }
        : null
    }))
  );
}

export const PdfBufferViewer = forwardRef<
  SearchablePdfViewerHandle,
  PdfBufferViewerProps
>(function PdfBufferViewer(
  {
    buffer,
    className,
    fileName,
    fallbackPdfPageItems,
    onCurrentPageChange,
    onAnnotationReferenceSelect,
    onTextSelectionMatch,
    sourceUrl
  },
  ref
) {
  const viewerRef = useRef<PDFViewerRef | null>(null);
  const fallbackPdfPageItemsRef =
    useRef<PdfFallbackPageItemsByPage>(fallbackPdfPageItems);
  const onCurrentPageChangeRef = useRef(onCurrentPageChange);
  const onAnnotationReferenceSelectRef = useRef(onAnnotationReferenceSelect);
  const onTextSelectionMatchRef = useRef(onTextSelectionMatch);
  const latestTextSelectionMatchRef =
    useRef<PdfViewerTextSelectionMatch | null>(null);
  const hydratedPreparedAnnotationBatchesRef =
    useRef<PdfViewerPreparedAnnotationBatchMap>({});
  const preparedAnnotationBatchesRef = useRef<
    Map<string, PreparedAnnotationBatch>
  >(new Map());
  const inFlightPreparedAnnotationBatchesRef = useRef<
    Map<
      string,
      {
        generation: number;
        promise: Promise<PreparedAnnotationBatch>;
        requestSignature: string;
      }
    >
  >(new Map());
  const prepareAnnotationBatchQueueRef = useRef<Promise<void>>(
    Promise.resolve()
  );
  const preparedAnnotationBatchGenerationRef = useRef(0);
  const annotationSelectionUnsubscribeRef = useRef<(() => void) | null>(null);
  const pageChangeUnsubscribeRef = useRef<(() => void) | null>(null);
  const textSelectionUnsubscribeRef = useRef<(() => void) | null>(null);
  const lastSelectedAnnotationIdRef = useRef<string | null>(null);
  const [documentBuffer, setDocumentBuffer] = useState<ArrayBuffer | null>(
    null
  );
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [viewerKey, setViewerKey] = useState(0);

  const restoreHydratedPreparedAnnotationBatches = useCallback(() => {
    const clonedBatches = clonePreparedAnnotationBatchMap(
      hydratedPreparedAnnotationBatchesRef.current
    );

    preparedAnnotationBatchesRef.current = new Map(
      Object.entries(clonedBatches).map(([cacheKey, batch]) => [
        cacheKey,
        {
          ...batch,
          cacheKey
        }
      ])
    );
  }, []);

  useEffect(() => {
    logPdfViewerMounted();
  }, []);

  useEffect(() => {
    if (sourceUrl) {
      setDocumentBuffer(null);
      setDocumentUrl(sourceUrl);
      setViewerKey((currentKey) => currentKey + 1);
      return;
    }

    if (!buffer) {
      setDocumentBuffer(null);
      setDocumentUrl(null);
      return;
    }

    setDocumentUrl(null);
    setDocumentBuffer(normalizePdfBuffer(buffer));
    setViewerKey((currentKey) => currentKey + 1);
  }, [buffer, fileName, sourceUrl]);

  useEffect(() => {
    preparedAnnotationBatchGenerationRef.current += 1;
    lastSelectedAnnotationIdRef.current = null;
    latestTextSelectionMatchRef.current = null;
    preparedAnnotationBatchesRef.current.clear();
    inFlightPreparedAnnotationBatchesRef.current.clear();
    annotationSelectionUnsubscribeRef.current?.();
    annotationSelectionUnsubscribeRef.current = null;
    pageChangeUnsubscribeRef.current?.();
    pageChangeUnsubscribeRef.current = null;
    textSelectionUnsubscribeRef.current?.();
    textSelectionUnsubscribeRef.current = null;
    restoreHydratedPreparedAnnotationBatches();

    return () => {
      annotationSelectionUnsubscribeRef.current?.();
      annotationSelectionUnsubscribeRef.current = null;
      pageChangeUnsubscribeRef.current?.();
      pageChangeUnsubscribeRef.current = null;
      textSelectionUnsubscribeRef.current?.();
      textSelectionUnsubscribeRef.current = null;
    };
  }, [restoreHydratedPreparedAnnotationBatches, viewerKey]);

  useEffect(() => {
    fallbackPdfPageItemsRef.current = fallbackPdfPageItems;
    preparedAnnotationBatchGenerationRef.current += 1;
    preparedAnnotationBatchesRef.current.clear();
    inFlightPreparedAnnotationBatchesRef.current.clear();
    restoreHydratedPreparedAnnotationBatches();
  }, [fallbackPdfPageItems, restoreHydratedPreparedAnnotationBatches]);

  useEffect(() => {
    onAnnotationReferenceSelectRef.current = onAnnotationReferenceSelect;
  }, [onAnnotationReferenceSelect]);

  useEffect(() => {
    onCurrentPageChangeRef.current = onCurrentPageChange;
  }, [onCurrentPageChange]);

  useEffect(() => {
    onTextSelectionMatchRef.current = onTextSelectionMatch;
  }, [onTextSelectionMatch]);

  useEffect(() => {
    if (sourceUrl) {
      return;
    }

    if (!documentBuffer) {
      setDocumentUrl(null);
      return;
    }

    const pdfBlob = new Blob([documentBuffer], { type: 'application/pdf' });
    const nextDocumentUrl = URL.createObjectURL(pdfBlob);

    setDocumentUrl(nextDocumentUrl);

    return () => {
      URL.revokeObjectURL(nextDocumentUrl);
    };
  }, [documentBuffer, sourceUrl]);

  const prepareAnnotationBatchInternal = async (
    cacheKey: string,
    requests: PdfViewerAnnotationRequest[]
  ): Promise<PreparedAnnotationBatch> => {
    const registry = await viewerRef.current?.registry;
    const search = registry ? getSearchCapability(registry) : undefined;
    const documentManager = registry
      ? getDocumentManagerCapability(registry)
      : undefined;
    const scroll = registry ? getScrollCapability(registry) : undefined;
    const documentId = documentManager?.getActiveDocumentId();

    if (!registry || !search || !documentId) {
      throw new Error('The PDF viewer is not ready for annotations yet.');
    }

    const totalPages = scroll?.forDocument(documentId).getTotalPages();
    const seenHighlightSignatures = new Set<string>();
    const seenFallbackSignatures = new Set<string>();
    const preparedDescriptors: PreparedAnnotationDescriptor[] = [];
    const normalizedQueryParts: string[] = [];
    let activePageNumber: number | null = null;
    let skippedAnnotations = 0;
    let totalResults = 0;

    for (const request of requests) {
      const normalizedQueries = normalizeQueries(request.query);
      const requestedPageNumbers = getRequestedPageNumbers(request);
      const requestedPageNumberSet =
        requestedPageNumbers.length > 0 ? new Set(requestedPageNumbers) : null;

      for (const requestedPageNumber of requestedPageNumbers) {
        if (!Number.isInteger(requestedPageNumber) || requestedPageNumber < 1) {
          throw new Error(
            'Fallback page number must be a whole number greater than zero.'
          );
        }

        if (totalPages !== undefined && requestedPageNumber > totalPages) {
          throw new Error(
            `Fallback page number must be between 1 and ${totalPages}.`
          );
        }
      }

      if (normalizedQueries.length === 0) {
        continue;
      }

      normalizedQueryParts.push(...normalizedQueries);

      for (const nextQuery of normalizedQueries) {
        const result = await search
          .searchAllPages(nextQuery, documentId)
          .toPromise();
        const matchingResults =
          requestedPageNumberSet === null
            ? result.results
            : result.results.filter((hit: { pageIndex: number }) =>
                requestedPageNumberSet.has(hit.pageIndex + 1)
              );
        const firstHit = matchingResults[0];
        const normalizedReference = cloneAnnotationReference(request.reference);
        logAnnotationNativeSearchResult(nextQuery, matchingResults.length);

        totalResults += matchingResults.length;

        if (activePageNumber === null && firstHit) {
          activePageNumber = firstHit.pageIndex + 1;
        }

        if (matchingResults.length > 0) {
          logFallbackSearchSkipped(nextQuery, matchingResults.length);

          for (const hit of matchingResults) {
            if (hit.rects.length === 0) {
              skippedAnnotations += 1;
              continue;
            }

            const signature = buildAnnotationSignature(
              nextQuery,
              hit.pageIndex,
              hit.charIndex,
              hit.charCount,
              normalizedReference?.candidateCardKey
            );

            if (seenHighlightSignatures.has(signature)) {
              skippedAnnotations += 1;
              continue;
            }

          preparedDescriptors.push({
            annotationStyle: request.annotationStyle,
            charCount: hit.charCount,
            charIndex: hit.charIndex,
            kind: 'highlight',
            pageIndex: hit.pageIndex,
            query: nextQuery,
              rects: hit.rects.map(cloneRect),
              reference: normalizedReference,
              signature
            });
            seenHighlightSignatures.add(signature);
          }

          continue;
        }

        const currentFallbackPdfPageItems = fallbackPdfPageItemsRef.current;

        logFallbackSearchRun(nextQuery, {
          fallbackItemCount: countPdfFallbackSearchInputItems(
            currentFallbackPdfPageItems,
            {
              pageNumber: request.pageNumber,
              pageNumbers: requestedPageNumbers
            }
          ),
          fallbackPageNumber: request.pageNumber,
          fallbackPageNumbers: requestedPageNumbers,
          nativeResultCount: matchingResults.length
        });
        console.log(
          'current fallback pdf page items',
          currentFallbackPdfPageItems
        );
        const fallbackResults = findPdfFallbackMatches(
          nextQuery,
          currentFallbackPdfPageItems,
          {
            pageNumber: request.pageNumber,
            pageNumbers: requestedPageNumbers
          }
        );
        logFallbackSearchResult(nextQuery, fallbackResults);
        const fallbackTargets =
          normalizeAnnotationStyle(request.annotationStyle) ===
          'active-selection'
            ? buildActiveSelectionFallbackBoundaries(fallbackResults)
            : fallbackResults.flatMap((result) => result.dimensionItems);
        const fallbackPageNumbers = Array.from(
          new Set(fallbackTargets.map((target) => target.pageNumber))
        ).sort(
          (leftPageNumber, rightPageNumber) => leftPageNumber - rightPageNumber
        );

        if (
          fallbackPageNumbers.length === 0 ||
          fallbackTargets.length === 0
        ) {
          if (activePageNumber === null && requestedPageNumbers.length > 0) {
            activePageNumber = requestedPageNumbers[0];
          }
          continue;
        }

        totalResults += fallbackTargets.length;

        if (activePageNumber === null) {
          activePageNumber = fallbackPageNumbers[0] ?? null;
        }

        for (const target of fallbackTargets) {
          const pageIndex = target.pageNumber - 1;
          const signature = buildRectangleAnnotationSignature(
            nextQuery,
            pageIndex,
            target,
            normalizedReference?.candidateCardKey
          );

          if (seenFallbackSignatures.has(signature)) {
            skippedAnnotations += 1;
            continue;
          }

          preparedDescriptors.push({
            annotationStyle: request.annotationStyle,
            kind: 'fallback',
            pageIndex,
            query: nextQuery,
            rect: cloneFallbackBoundary(target),
            reference: normalizedReference,
            signature
          });
          seenFallbackSignatures.add(signature);
        }
      }
    }

    search.stopSearch(documentId);

    return {
      activePageNumber,
      cacheKey,
      descriptors: preparedDescriptors,
      query: Array.from(new Set(normalizedQueryParts)).join(', '),
      requestSignature: '',
      skippedAnnotations,
      totalResults
    };
  };

  const applyPreparedAnnotationBatchInternal = async (
    batch: PreparedAnnotationBatch,
    {
      pageNumber,
      replaceActiveSelectionAnnotations = false,
      scrollToPage = true
    }: PdfViewerApplyPreparedAnnotationBatchOptions = {}
  ): Promise<PdfViewerAnnotationResult> => {
    const registry = await viewerRef.current?.registry;
    const annotation = registry ? getAnnotationCapability(registry) : undefined;
    const documentManager = registry
      ? getDocumentManagerCapability(registry)
      : undefined;
    const scroll = registry ? getScrollCapability(registry) : undefined;
    const documentId = documentManager?.getActiveDocumentId();

    if (!registry || !annotation || !documentId) {
      throw new Error('The PDF viewer is not ready for annotations yet.');
    }

    if (replaceActiveSelectionAnnotations) {
      const activeSelectionAnnotations = getAnnotationsBySource(annotation, [
        PROGRAMMATIC_ACTIVE_SEARCH_ANNOTATION_SOURCE,
        PROGRAMMATIC_ACTIVE_FALLBACK_SEARCH_ANNOTATION_SOURCE
      ]).map(({ pageIndex, object }: TrackedAnnotation) => ({
        id: object.id,
        pageIndex
      }));

      if (activeSelectionAnnotations.length > 0) {
        annotation.deleteAnnotations(activeSelectionAnnotations);
        await annotation.commit().toPromise();
      }
    }

    const existingDefaultHighlightSignatures = getExistingAnnotationSignatures(
      annotation,
      PROGRAMMATIC_SEARCH_ANNOTATION_SOURCE
    );
    const existingActiveHighlightSignatures = getExistingAnnotationSignatures(
      annotation,
      PROGRAMMATIC_ACTIVE_SEARCH_ANNOTATION_SOURCE
    );
    const existingDefaultFallbackSignatures = getExistingAnnotationSignatures(
      annotation,
      PROGRAMMATIC_FALLBACK_SEARCH_ANNOTATION_SOURCE
    );
    const existingActiveFallbackSignatures = getExistingAnnotationSignatures(
      annotation,
      PROGRAMMATIC_ACTIVE_FALLBACK_SEARCH_ANNOTATION_SOURCE
    );
    const normalizedPageIndex =
      typeof pageNumber === 'number' &&
      Number.isInteger(pageNumber) &&
      pageNumber > 0
        ? pageNumber - 1
        : null;
    const descriptorsToApply =
      normalizedPageIndex === null
        ? batch.descriptors
        : batch.descriptors.filter(
            (descriptor) => descriptor.pageIndex === normalizedPageIndex
          );
    const activePageNumber: number | null =
      normalizedPageIndex === null
        ? batch.activePageNumber
        : (pageNumber ?? null);
    let createdAnnotations = 0;
    let skippedAnnotations = batch.skippedAnnotations;

    if (normalizedPageIndex !== null) {
      logPdfViewerPageBatchApply({
        cacheKey: batch.cacheKey,
        descriptorsToApply: descriptorsToApply.length,
        pageNumber: activePageNumber,
        totalBatchDescriptors: batch.descriptors.length
      });
    }

    for (const descriptor of descriptorsToApply) {
      if (descriptor.kind === 'highlight') {
        const existingHighlightSignatures =
          normalizeAnnotationStyle(descriptor.annotationStyle) ===
          'active-selection'
            ? existingActiveHighlightSignatures
            : existingDefaultHighlightSignatures;

        if (existingHighlightSignatures.has(descriptor.signature)) {
          skippedAnnotations += 1;
          continue;
        }

        annotation.createAnnotation(
          descriptor.pageIndex,
          createHighlightAnnotation(
            descriptor.query,
            descriptor.pageIndex,
            descriptor.charIndex,
            descriptor.charCount,
            descriptor.rects,
            descriptor.reference,
            descriptor.annotationStyle
          )
        );
        existingHighlightSignatures.add(descriptor.signature);
        createdAnnotations += 1;
        continue;
      }

      const existingFallbackSignatures =
        normalizeAnnotationStyle(descriptor.annotationStyle) ===
        'active-selection'
          ? existingActiveFallbackSignatures
          : existingDefaultFallbackSignatures;

      if (existingFallbackSignatures.has(descriptor.signature)) {
        skippedAnnotations += 1;
        continue;
      }

      annotation.createAnnotation(
        descriptor.pageIndex,
        createFallbackSearchRectangleAnnotation(
          descriptor.query,
          descriptor.pageIndex,
          descriptor.rect,
          descriptor.reference,
          descriptor.annotationStyle
        )
      );
      existingFallbackSignatures.add(descriptor.signature);
      createdAnnotations += 1;
    }

    if (createdAnnotations > 0) {
      await annotation.commit().toPromise();
    }

    if (scrollToPage && activePageNumber && scroll) {
      scroll.forDocument(documentId).scrollToPage({
        behavior: 'smooth',
        pageNumber: activePageNumber
      });
    }

    return {
      activePageNumber,
      createdAnnotations,
      query: batch.query,
      skippedAnnotations,
      totalResults: batch.totalResults
    };
  };

  const resolveCurrentTextSelectionMatch = useCallback(
    async (
      registry: PluginRegistry,
      documentId: string
    ): Promise<PdfViewerTextSelectionMatch | null> => {
      const selection = getSelectionCapability(registry);

      if (!selection) {
        return null;
      }

      const sourceText = normalizeSelectedText(
        await selection.getSelectedText(documentId).toPromise()
      );

      if (!sourceText) {
        return null;
      }

      const formattedSelection = selection.getFormattedSelection(documentId);
      const pageNumbers = formattedSelection
        .map((entry: { pageIndex: number }): number | null =>
          Number.isInteger(entry.pageIndex) ? entry.pageIndex + 1 : null
        )
        .filter(
          (pageNumber: number | null): pageNumber is number =>
            pageNumber !== null
        );
      const selectedPageNumbers = Array.from(new Set<number>(pageNumbers)).sort(
        (leftPageNumber, rightPageNumber) => leftPageNumber - rightPageNumber
      );

      return {
        pageNumbers: selectedPageNumbers,
        sourceText
      };
    },
    []
  );

  // Type guard to check if an item is a GroupItem
  function isGroupItem(item: ToolbarItem): item is GroupItem {
    return item.type === 'group';
  }

  const setSelectionToolbarVisibility = useCallback(
    (
      ui: ReturnType<UIPlugin['provides']>,
      commands: ReturnType<CommandsPlugin['provides']>,
      hasTextSelection: boolean
    ) => {
      if (hasTextSelection) {
        ui.enableCategory(TEXT_SELECTION_TOOLBAR_CATEGORY);
        commands.enableCategory(TEXT_SELECTION_TOOLBAR_CATEGORY);
        return;
      }

      ui.disableCategory(TEXT_SELECTION_TOOLBAR_CATEGORY);
      commands.disableCategory(TEXT_SELECTION_TOOLBAR_CATEGORY);
    },
    []
  );

  // Setup custom commands and UI when viewer is ready
  const handleReady = useCallback(
    async (registry: PluginRegistry) => {
      const container = viewerRef.current?.container;
      if (!container) {
        return;
      }

      const commands = registry
        .getPlugin<CommandsPlugin>('commands')
        ?.provides();
      const ui = registry.getPlugin<UIPlugin>('ui')?.provides();

      if (!commands || !ui) {
        return;
      }

      // ─────────────────────────────────────────────────────────
      // 1. Register custom icons
      // ─────────────────────────────────────────────────────────
      container.registerIcons({
        manualAdd: {
          viewBox: '0 0 24 24',
          paths: [
            {
              d: 'M12 22a10 10 0 1 0 0-20a10 10 0 1 0 0 20Z',
              stroke: '#56dac5',
              strokeWidth: 2,
              fill: 'none'
            },
            {
              d: 'M8 12h8',
              stroke: '#56dac5',
              strokeWidth: 2,
              strokeLinecap: 'round',
              fill: 'none'
            },
            {
              d: 'M12 8v8',
              stroke: '#56dac5',
              strokeWidth: 2,
              strokeLinecap: 'round',
              fill: 'none'
            }
          ]
        }
      });

      // ─────────────────────────────────────────────────────────
      // 2. Register custom commands
      // ─────────────────────────────────────────────────────────
      if (
        !commands
          .getAllCommands()
          .some(
            (command: { id: string }) => command.id === CUSTOM_SMILEY_COMMAND_ID
          )
      ) {
        commands.registerCommand({
          id: CUSTOM_SMILEY_COMMAND_ID,
          label: 'Create Policy Rule',
          icon: 'manualAdd',
          categories: [TEXT_SELECTION_TOOLBAR_CATEGORY],
          action: ({
            documentId: activeDocumentId,
            registry: activeRegistry
          }: {
            documentId: string;
            registry: PluginRegistry;
          }) => {
            void (async () => {
              const nextMatch =
                (await resolveCurrentTextSelectionMatch(
                  activeRegistry,
                  activeDocumentId
                )) ?? latestTextSelectionMatchRef.current;

              if (!nextMatch) {
                return;
              }

              latestTextSelectionMatchRef.current = nextMatch;
              onTextSelectionMatchRef.current?.(nextMatch);
            })();
          }
        });
      }

      // ─────────────────────────────────────────────────────────
      // 3. Modify the UI: add the custom button to the toolbar
      // ─────────────────────────────────────────────────────────
      const currentSchema = ui.getSchema();
      const mainToolbar = currentSchema.toolbars['main-toolbar'];

      if (mainToolbar) {
        // Clone items with proper typing using structuredClone
        const items: ToolbarItem[] = structuredClone(mainToolbar.items);

        // Find the left-group using type guard
        const leftGroup = items.find(
          (item): item is GroupItem =>
            isGroupItem(item) && item.id === 'left-group'
        );

        if (
          leftGroup &&
          !leftGroup.items.some(
            (item: { id: string }) => item.id === CUSTOM_SMILEY_BUTTON_ID
          )
        ) {
          leftGroup.items.push({
            type: 'command-button',
            id: CUSTOM_SMILEY_BUTTON_ID,
            commandId: CUSTOM_SMILEY_COMMAND_ID,
            variant: 'icon',
            categories: [TEXT_SELECTION_TOOLBAR_CATEGORY]
          });

          ui.mergeSchema({
            toolbars: {
              'main-toolbar': {
                ...mainToolbar,
                items
              }
            }
          });
        }
      }

      setSelectionToolbarVisibility(ui, commands, false);
    },
    [resolveCurrentTextSelectionMatch, setSelectionToolbarVisibility]
  );

  useImperativeHandle(ref, () => {
    logPdfViewerImperativeHandleInitialized();

    return {
      annotateMatches: async ({
        annotationStyle,
        pageNumber,
        pageNumbers,
        query,
        reference,
        scrollToPage = true
      }: PdfViewerAnnotationRequest) => {
        logAnnotationSearchStart(query, pageNumber, pageNumbers);
        const batch = await prepareAnnotationBatchInternal(
          '__single-annotation__',
          [
            {
              annotationStyle,
              pageNumber,
              pageNumbers,
              query,
              reference
            }
          ]
        );

        return applyPreparedAnnotationBatchInternal(batch, {
          replaceActiveSelectionAnnotations:
            normalizeAnnotationStyle(annotationStyle) === 'active-selection',
          scrollToPage
        });
      },
      applyPreparedAnnotationBatch: async (
        cacheKey: string,
        options?: PdfViewerApplyPreparedAnnotationBatchOptions
      ) => {
        const batch = preparedAnnotationBatchesRef.current.get(cacheKey);

        if (!batch) {
          throw new Error(
            `No prepared annotation batch found for ${cacheKey}.`
          );
        }

        return applyPreparedAnnotationBatchInternal(batch, options);
      },
      clearAnnotations: async (
        mode: PdfViewerClearAnnotationsMode = 'programmatic'
      ) => {
        logPdfViewerClearAnnotationsCalled(mode);
        const registry = await viewerRef.current?.registry;
        const annotation = registry
          ? getAnnotationCapability(registry)
          : undefined;
        const search = registry ? getSearchCapability(registry) : undefined;
        const documentManager = registry
          ? getDocumentManagerCapability(registry)
          : undefined;
        const documentId = documentManager?.getActiveDocumentId();

        if (!annotation) {
          return;
        }

        if (search && documentId) {
          search.stopSearch(documentId);
        }

        lastSelectedAnnotationIdRef.current = null;
        annotation.deselectAnnotation();

        if (mode === 'all') {
          annotation.deleteAllAnnotations();
          await annotation.commit().toPromise();
          return;
        }

        const annotationsToDelete = getProgrammaticAnnotations(annotation).map(
          ({ pageIndex, object }: TrackedAnnotation) => ({
            id: object.id,
            pageIndex
          })
        );

        if (annotationsToDelete.length === 0) {
          return;
        }

        annotation.deleteAnnotations(annotationsToDelete);
        await annotation.commit().toPromise();
      },
      removeMatchingAnnotations: async ({
        annotationStyle,
        pageNumber,
        pageNumbers,
        query,
        reference
      }: PdfViewerAnnotationRequest) => {
        const registry = await viewerRef.current?.registry;
        const annotation = registry
          ? getAnnotationCapability(registry)
          : undefined;
        const documentManager = registry
          ? getDocumentManagerCapability(registry)
          : undefined;
        const documentId = documentManager?.getActiveDocumentId();

        if (!registry || !annotation || !documentId) {
          throw new Error('The PDF viewer is not ready for annotations yet.');
        }

        const batch = await prepareAnnotationBatchInternal(
          '__single-annotation-removal__',
          [
            {
              annotationStyle,
              pageNumber,
              pageNumbers,
              query,
              reference
            }
          ]
        );
        const annotationKeysToDelete = new Set(
          batch.descriptors.map(buildPreparedAnnotationDeletionKey)
        );

        if (annotationKeysToDelete.size === 0) {
          return 0;
        }

        const annotationsToDelete = annotation
          .getAnnotations()
          .filter((trackedAnnotation: TrackedAnnotation) => {
            const custom = trackedAnnotation.object.custom;

            if (
              !custom ||
              typeof custom !== 'object' ||
              !('source' in custom) ||
              !('signature' in custom) ||
              typeof custom.source !== 'string' ||
              typeof custom.signature !== 'string'
            ) {
              return false;
            }

            return annotationKeysToDelete.has(
              [
                trackedAnnotation.pageIndex,
                custom.source,
                custom.signature
              ].join('::')
            );
          })
          .map(({ pageIndex, object }: TrackedAnnotation) => ({
            id: object.id,
            pageIndex
          }));

        if (annotationsToDelete.length === 0) {
          return 0;
        }

        annotation.deleteAnnotations(annotationsToDelete);
        await annotation.commit().toPromise();

        return annotationsToDelete.length;
      },
      prepareAnnotationBatch: async (
        cacheKey: string,
        requests: PdfViewerAnnotationRequest[]
      ) => {
        const requestSignature =
          buildPreparedAnnotationBatchRequestSignature(requests);
        const generation = preparedAnnotationBatchGenerationRef.current;
        const existingBatch =
          preparedAnnotationBatchesRef.current.get(cacheKey);

        if (existingBatch?.requestSignature === requestSignature) {
          return buildPreparedAnnotationBatchResult(existingBatch);
        }

        const inFlightBatch =
          inFlightPreparedAnnotationBatchesRef.current.get(cacheKey);

        if (
          inFlightBatch &&
          inFlightBatch.generation === generation &&
          inFlightBatch.requestSignature === requestSignature
        ) {
          const batch = await inFlightBatch.promise;
          return buildPreparedAnnotationBatchResult(batch);
        }

        const queuedPreparation = prepareAnnotationBatchQueueRef.current
          .catch(() => undefined)
          .then(async () => {
            const preparedBatch = await prepareAnnotationBatchInternal(
              cacheKey,
              requests
            );
            const batch = {
              ...preparedBatch,
              requestSignature
            };

            if (preparedAnnotationBatchGenerationRef.current === generation) {
              preparedAnnotationBatchesRef.current.set(cacheKey, batch);
            }

            return batch;
          });

        prepareAnnotationBatchQueueRef.current = queuedPreparation.then(
          () => undefined,
          () => undefined
        );
        inFlightPreparedAnnotationBatchesRef.current.set(cacheKey, {
          generation,
          promise: queuedPreparation,
          requestSignature
        });

        try {
          const batch = await queuedPreparation;
          return buildPreparedAnnotationBatchResult(batch);
        } finally {
          const currentBatch =
            inFlightPreparedAnnotationBatchesRef.current.get(cacheKey);

          if (currentBatch?.promise === queuedPreparation) {
            inFlightPreparedAnnotationBatchesRef.current.delete(cacheKey);
          }
        }
      },
      exportPreparedAnnotationBatches: () =>
        Object.fromEntries(
          Array.from(preparedAnnotationBatchesRef.current.entries()).map(
            ([cacheKey, batch]) => [
              cacheKey,
              clonePreparedAnnotationBatch({
                ...batch,
                cacheKey
              })
            ]
          )
        ),
      hydratePreparedAnnotationBatches: (
        batches: PdfViewerPreparedAnnotationBatchMap
      ) => {
        const clonedBatches = clonePreparedAnnotationBatchMap(batches);

        hydratedPreparedAnnotationBatchesRef.current = clonedBatches;
        preparedAnnotationBatchGenerationRef.current += 1;
        inFlightPreparedAnnotationBatchesRef.current.clear();
        preparedAnnotationBatchesRef.current = new Map(
          Object.entries(clonedBatches).map(([cacheKey, batch]) => [
            cacheKey,
            {
              ...batch,
              cacheKey
            }
          ])
        );
      },
      createRectangleAnnotation: async (
        rect: PdfViewerRectangleAnnotationInput
      ) => {
        logPdfViewerRectangleAnnotationCalled(rect);
        const registry = await viewerRef.current?.registry;
        const annotation = registry
          ? getAnnotationCapability(registry)
          : undefined;
        const documentManager = registry
          ? getDocumentManagerCapability(registry)
          : undefined;
        const scroll = registry ? getScrollCapability(registry) : undefined;
        const documentId = documentManager?.getActiveDocumentId();

        if (!registry || !annotation || !scroll || !documentId) {
          throw new Error(
            'The PDF viewer is not ready for rectangle annotations yet.'
          );
        }

        const totalPages = scroll.forDocument(documentId).getTotalPages();

        if (
          !Number.isInteger(rect.pageNumber) ||
          rect.pageNumber < 1 ||
          rect.pageNumber > totalPages
        ) {
          throw new Error(`Page number must be between 1 and ${totalPages}.`);
        }

        const pageIndex = rect.pageNumber - 1;
        const nextAnnotation = createRectangleAnnotation(rect, pageIndex);

        annotation.createAnnotation(pageIndex, nextAnnotation);
        await annotation.commit().toPromise();
        scroll.forDocument(documentId).scrollToPage({
          behavior: 'smooth',
          pageNumber: rect.pageNumber
        });

        return {
          annotationId: nextAnnotation.id,
          pageNumber: rect.pageNumber
        } satisfies PdfViewerRectangleAnnotationResult;
      },
      clearSearch: async () => {
        logPdfViewerClearSearchCalled();
        const registry = await viewerRef.current?.registry;
        const search = registry ? getSearchCapability(registry) : undefined;
        const documentManager = registry
          ? getDocumentManagerCapability(registry)
          : undefined;
        const documentId = documentManager?.getActiveDocumentId();

        if (!search || !documentId) {
          return;
        }

        search.stopSearch(documentId);
      },
      search: async (query: string) => {
        const normalizedQuery = normalizeQueryText(query);
        logPdfViewerSearchCalled(normalizedQuery);
        const registry = await viewerRef.current?.registry;
        const search = registry ? getSearchCapability(registry) : undefined;
        const documentManager = registry
          ? getDocumentManagerCapability(registry)
          : undefined;
        const scroll = registry ? getScrollCapability(registry) : undefined;
        const documentId = documentManager?.getActiveDocumentId();

        if (!registry || !search || !documentId) {
          throw new Error('The PDF viewer is not ready yet.');
        }

        if (!normalizedQuery) {
          search.stopSearch(documentId);

          return {
            activePageNumber: null,
            query: '',
            totalResults: 0
          };
        }

        search.setShowAllResults(true, documentId);

        const result = await search
          .searchAllPages(normalizedQuery, documentId)
          .toPromise();
        const firstHit = result.results[0];
        logPdfViewerSearchResult(normalizedQuery, result.total);

        if (firstHit && scroll) {
          scroll.forDocument(documentId).scrollToPage({
            behavior: 'smooth',
            pageNumber: firstHit.pageIndex + 1
          });
        }

        return {
          activePageNumber: firstHit ? firstHit.pageIndex + 1 : null,
          query: normalizedQuery,
          totalResults: result.total
        };
      }
    };
  }, []);

  if (!documentBuffer || !documentUrl) {
    return (
      <div
        className={clsx(
          'flex h-full w-full items-center justify-center bg-white text-sm text-slate-500',
          className
        )}
      >
        Loading PDF...
      </div>
    );
  }

  const config: PDFViewerConfig = {
    disabledCategories: [...DISABLED_VIEWER_CATEGORIES],
    export: {
      defaultFileName: fileName
    },
    zoom: {
      defaultZoomLevel: ZoomMode.Automatic
    },
    fontFallback: null,
    fonts: {
      signature: null,
      ui: null
    },
    search: {
      showAllResults: true
    },
    src: documentUrl,
    tabBar: 'never',
    theme: embedPdfTheme,
    worker: true
  };

  return (
    <div
      className={clsx(
        'pdf-viewer-shell relative h-full w-full min-h-0',
        className
      )}
    >
      <PDFViewer
        className="block h-full w-full"
        config={config}
        key={`${fileName}-${viewerKey}`}
        onInit={installSearchHighlightStyles}
        onReady={(registry) => {
          annotationSelectionUnsubscribeRef.current?.();
          annotationSelectionUnsubscribeRef.current = null;
          pageChangeUnsubscribeRef.current?.();
          pageChangeUnsubscribeRef.current = null;
          textSelectionUnsubscribeRef.current?.();
          textSelectionUnsubscribeRef.current = null;
          lastSelectedAnnotationIdRef.current = null;

          const selection = getSelectionCapability(registry);

          const annotation: AnnotationCapability | undefined =
            getAnnotationCapability(registry);
          const documentManager = getDocumentManagerCapability(registry);
          const scroll = getScrollCapability(registry);
          const documentId = documentManager?.getActiveDocumentId();

          if (scroll && documentId) {
            onCurrentPageChangeRef.current?.(
              scroll.forDocument(documentId).getCurrentPage()
            );

            pageChangeUnsubscribeRef.current = scroll.onPageChange(
              (event: PageChangeEvent) => {
                if (event.documentId !== documentId) {
                  return;
                }

                onCurrentPageChangeRef.current?.(event.pageNumber);
              }
            );
          }

          if (selection && documentId) {
            const commands = registry
              .getPlugin<CommandsPlugin>('commands')
              ?.provides();
            const ui = registry.getPlugin<UIPlugin>('ui')?.provides();
            const selectionScope = selection.forDocument(documentId);

            if (commands && ui) {
              setSelectionToolbarVisibility(
                ui,
                commands,
                Boolean(selectionScope.getState().selection)
              );

              const unsubscribeSelectionChange =
                selectionScope.onSelectionChange((nextSelection: any) => {
                  setSelectionToolbarVisibility(
                    ui,
                    commands,
                    Boolean(nextSelection)
                  );

                  if (!nextSelection) {
                    latestTextSelectionMatchRef.current = null;
                  }
                });
              const unsubscribeSelectionEnd = selectionScope.onEndSelection(
                () => {
                  void resolveCurrentTextSelectionMatch(
                    registry,
                    documentId
                  ).then((nextMatch) => {
                    latestTextSelectionMatchRef.current = nextMatch;
                  });
                }
              );

              textSelectionUnsubscribeRef.current = () => {
                unsubscribeSelectionChange();
                unsubscribeSelectionEnd();
              };
            }
          }

          if (!annotation) {
            return;
          }

          annotationSelectionUnsubscribeRef.current = annotation.onStateChange(
            ({ state }: { state: any }) => {
              const selectedAnnotationId = state.selectedUids[0] ?? null;

              if (!selectedAnnotationId) {
                lastSelectedAnnotationIdRef.current = null;
                return;
              }

              if (
                selectedAnnotationId === lastSelectedAnnotationIdRef.current
              ) {
                return;
              }

              lastSelectedAnnotationIdRef.current = selectedAnnotationId;

              const trackedAnnotation = annotation
                .getAnnotations()
                .find(
                  ({ object }: TrackedAnnotation) =>
                    object.id === selectedAnnotationId
                );

              if (!trackedAnnotation) {
                return;
              }

              const reference = getAnnotationReferenceFromCustom(
                trackedAnnotation.object.custom
              );

              if (!reference) {
                return;
              }

              onAnnotationReferenceSelectRef.current?.({
                annotationId: selectedAnnotationId,
                annotationStyle: getAnnotationStyleFromCustom(
                  trackedAnnotation.object.custom
                ),
                pageNumber: trackedAnnotation.pageIndex + 1,
                reference,
                selectionKey: createAnnotationId()
              });
            }
          );
          void handleReady(registry);
        }}
        ref={viewerRef}
        style={{ height: '100%', width: '100%' }}
      />
    </div>
  );
});
